import * as XLSX from "xlsx";

const normalizeHeader = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const CIRO_SHEET_HINTS = {
  team: "Hoca ve ekip listesi",
  operations: "CİRO TAKİP",
  hospitalPayments: "ATAŞEHİR HASTA VE ÖDEME",
};

export const findWorksheetByHint = (workbook, hint) => {
  const target = hint.toLowerCase();
  return (
    workbook.SheetNames.find((name) => normalizeHeader(name).includes(target)) ||
    workbook.SheetNames[0]
  );
};

export const sheetToMatrix = (worksheet) => {
  const ref = worksheet["!ref"];
  if (!ref) return [];
  const range = XLSX.utils.decode_range(ref);
  const rows = [];
  for (let r = range.s.r; r <= range.e.r; r += 1) {
    const row = [];
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
      row.push(normalizeExcelCellValue(cell?.v ?? null, cell));
    }
    rows.push(row);
  }
  return rows;
};

const parseTurkishDateString = (value) => {
  const str = String(value ?? "").trim();
  const dotMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) {
    const [, d, m, y] = dotMatch;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch && !str.includes("T")) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  return null;
};

const formatDateInTimeZone = (date, timeZone = "Europe/Istanbul") => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return y && m && d ? `${y}-${m}-${d}` : null;
};

/** Excel hücresi -> YYYY-MM-DD (timezone kayması olmadan) */
export const normalizeExcelCellValue = (value, cell = null) => {
  if (cell?.t === "d" && cell?.w) {
    const fromDisplay = parseTurkishDateString(cell.w);
    if (fromDisplay) return fromDisplay;
  }

  if (typeof value === "string") {
    const fromText = parseTurkishDateString(value);
    if (fromText) return fromText;
    if (/^\d{4}-\d{2}-\d{2}T/i.test(value)) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return formatDateInTimeZone(parsed) ?? value;
      }
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDateInTimeZone(value) ?? null;
  }

  if (typeof value === "number" && value > 30000 && value < 60000) {
    const epoch = new Date(Date.UTC(1899, 11, 30));
    epoch.setUTCDate(epoch.getUTCDate() + Math.floor(value));
    return formatDateInTimeZone(epoch) ?? null;
  }

  return value ?? null;
};

export const normalizeImportRows = (rows) =>
  (rows || []).map((row) =>
    Array.isArray(row) ? row.map((cell) => normalizeExcelCellValue(cell)) : row
  );

export const parseTeamListWorkbook = (workbook) => {
  const sheetName = workbook.SheetNames[0];
  const matrix = sheetToMatrix(workbook.Sheets[sheetName]);
  const headerIdx = matrix.findIndex((row) => normalizeHeader(row[0]).includes("hastane"));
  const start = headerIdx >= 0 ? headerIdx : 0;
  return matrix.slice(start).filter((row) => row.some((cell) => cell !== null && String(cell).trim() !== ""));
};

export const parseDataSheetRows = (workbook, hint) => {
  const sheetName = findWorksheetByHint(workbook, hint);
  const matrix = sheetToMatrix(workbook.Sheets[sheetName]);
  if (matrix.length <= 1) return { sheetName, rows: [] };
  return { sheetName, rows: matrix.slice(1) };
};

export const readWorkbookFromFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        resolve(workbook);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Dosya okunamadı"));
    reader.readAsArrayBuffer(file);
  });

export const formatCiroMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatCiroDate = (value) => {
  if (!value) return "—";
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const [y, m, d] = str.slice(0, 10).split("-");
    return `${d}.${m}.${y}`;
  }
  const dotMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) {
    const [, d, m, y] = dotMatch;
    return `${d.padStart(2, "0")}.${m.padStart(2, "0")}.${y}`;
  }
  return str;
};

export const summarizeImportDateRange = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const dates = rows
    .map((row) => row?.[0])
    .filter(Boolean)
    .map((value) => {
      const str = String(value);
      if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
      const dot = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (dot) {
        const [, d, m, y] = dot;
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
      return null;
    })
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return null;
  return { min: dates[0], max: dates[dates.length - 1] };
};

export const CEVDET_DEFAULT_SHEETS = {
  operations: {
    label: "Cevdet Düger — Ciro Takip",
    sheet_url: "https://docs.google.com/spreadsheets/d/1SIEW3KFLT60e_OftGA4sEJCHQtQ7LWlTIIBxN9LI8yM/edit?gid=1083963115#gid=1083963115",
    sheet_type: "ciro_takip",
  },
  hospitalPayments: {
    label: "Cevdet Düger — Atasehir Hasta Odeme",
    sheet_url: "https://docs.google.com/spreadsheets/d/1SIEW3KFLT60e_OftGA4sEJCHQtQ7LWlTIIBxN9LI8yM/edit?gid=0",
    sheet_type: "hospital_payments",
  },
};
